/**
 * The month-end statement, print-first: /accounting/statement/YYYY-MM (admin).
 * With ?print=1 it opens the browser's print dialog once laid out — "Download
 * PDF" is Save as PDF from there; there is no server-side PDF library.
 *
 * LETTER PAGES, LAID OUT HERE. Every row has a fixed height (one line, clipped
 * with an ellipsis; the Excel file carries every word), so pages are filled by
 * arithmetic, not measurement: a row is never split, a section that runs on
 * repeats its heading with "(continued)", and each page carries the slim header
 * (month, Page X of N) and the footer. Page 1 is two columns — the sections on
 * the left, "Where we stand" and "Partners" on the right; what does not fit on
 * the right continues after the sections.
 *
 * The figures come from GET /accounting/statements/:month (lib/statement.ts) —
 * the closed snapshot, or live figures for an open month. Nothing is computed
 * here. Like the client PDFs, a printed document keeps its own colours (navy
 * #14213d, amber #f2a93b), not the CRM theme's.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../hooks/useApi';
import { formatMoney, formatDate, formatDateTime } from '../lib/utils';
import { ReportTabs } from './JobReviewPrint';

const NAVY = '#14213d';
const AMBER = '#f2a93b';
const INK = '#1b2233';
const MUTED = '#5f6778';
const RULE = '#e3e5ea';
const MONO = "'DM Mono', ui-monospace, monospace";

// ---- Geometry (CSS px; letter = 816 × 1056 at 96 dpi) ------------------------
const PAGE_W = 816, PAGE_H = 1056, MARGIN = 40;
const SLIM = 26, SLIM_GAP = 10, FOOT = 22;
const BAND = 104, BAND_GAP = 14, TILES = 70, TILES_GAP = 16;
const LEFT_W = 452, COL_GAP = 20;
const CONTENT_H = PAGE_H - 2 * MARGIN;
const CAP_FIRST = CONTENT_H - SLIM - SLIM_GAP - BAND - BAND_GAP - TILES - TILES_GAP - FOOT;
const CAP_REST = CONTENT_H - SLIM - SLIM_GAP - FOOT;
const H = { head: 26, line: 18, sub: 20, note: 18, kv: 19, empty: 18, gap: 12 };

// ---- Rows and sections --------------------------------------------------------
type Row =
  | { t: 'line'; cells: React.ReactNode[] }
  | { t: 'sub'; label: string; amount?: number }
  | { t: 'kv'; label: string; amount: number | null; strong?: boolean; indent?: boolean }
  | { t: 'note'; text: string };

interface Section {
  key: string;
  title: string;
  total?: number | null;
  /** grid-template-columns of its 'line' rows */
  cols?: string;
  /** right-aligned columns of its 'line' rows */
  right?: number[];
  rows: Row[];
  /** The first piece is itself a continuation (the right column's overflow). */
  startsContinued?: boolean;
}
interface Piece { section: Section; continued: boolean; rows: Row[] }

const rowH = (r: Row) => H[r.t];

/**
 * Fill frames of the given capacities with sections. A heading never sits
 * alone at the bottom (it needs its first row with it), nor a subheading.
 * With `maxFrames`, what does not fit comes back as `rest` — sections whose
 * first piece is a continuation.
 */
function paginate(sections: Section[], cap: (i: number) => number, maxFrames = Infinity): { frames: Piece[][]; rest: Section[] } {
  const frames: Piece[][] = [[]];
  let used = 0;
  const fresh = () => { frames.push([]); used = 0; };
  for (let si = 0; si < sections.length; si++) {
    const s = sections[si];
    let i = 0, first = true;
    while (first || i < s.rows.length) {
      const continued = !first || !!s.startsContinued;
      const lead = s.rows.length ? rowH(s.rows[i]) + (s.rows[i].t === 'sub' && s.rows[i + 1] ? rowH(s.rows[i + 1]) : 0) : H.empty;
      const gap = used > 0 ? H.gap : 0;
      if (used + gap + H.head + lead > cap(frames.length - 1)) {
        if (used === 0) { /* cannot fit even on a fresh frame: place anyway */ }
        else if (frames.length >= maxFrames) {
          return { frames, rest: [{ ...s, rows: s.rows.slice(i), startsContinued: continued }, ...sections.slice(si + 1)] };
        } else { fresh(); continue; }
      }
      used += (used > 0 ? H.gap : 0) + H.head;
      const piece: Piece = { section: s, continued, rows: [] };
      if (!s.rows.length) used += H.empty;
      while (i < s.rows.length) {
        const r = s.rows[i];
        const keep = r.t === 'sub' && s.rows[i + 1] ? rowH(s.rows[i + 1]) : 0;
        if (piece.rows.length && used + rowH(r) + keep > cap(frames.length - 1)) break;
        if (!piece.rows.length && used + rowH(r) > cap(frames.length - 1)) break;
        piece.rows.push(r); used += rowH(r); i++;
      }
      frames[frames.length - 1].push(piece);
      first = false;
      if (i < s.rows.length) {
        if (frames.length >= maxFrames) {
          return { frames, rest: [{ ...s, rows: s.rows.slice(i), startsContinued: true }, ...sections.slice(si + 1)] };
        }
        fresh();
      }
    }
  }
  return { frames, rest: [] };
}

const money = (v: number | null | undefined) => (v == null ? '—' : formatMoney(v));
const jobs = (n?: number, word = 'job') => (n ? `${n} ${word}${n === 1 ? '' : 's'}` : '');
const dim = (text: React.ReactNode) => <span style={{ color: MUTED }}>{text}</span>;

function buildSections(s: any): { left: Section[]; right: Section[] } {
  const sec = s.sections;
  const left: Section[] = [
    {
      key: 'income', title: 'Income from clients', total: sec.income.total, cols: '52px 1fr 78px 84px', right: [3],
      rows: sec.income.lines.map((l: any) => ({ t: 'line', cells: [
        l.jobNo ?? '', <>{l.client ?? l.label}{l.note ? dim(` · ${l.note}`) : null}</>, formatDate(l.date), money(l.amount),
      ] })),
    },
    {
      key: 'deposits', title: 'Deposits received', total: sec.deposits.total, cols: '52px 1fr 78px 70px 84px', right: [4],
      rows: sec.deposits.lines.map((l: any) => ({ t: 'line', cells: [
        l.jobNo ?? '', <>{l.client}{l.note ? dim(` · ${l.note}`) : null}</>, formatDate(l.date), l.method || '—', money(l.amount),
      ] })),
    },
    {
      key: 'expenses', title: 'Expenses & taxes', total: sec.expenses.total, cols: '78px 1fr 84px', right: [2],
      rows: sec.expenses.groups.flatMap((g: any) => [
        { t: 'sub', label: g.label, amount: g.total } as Row,
        ...g.lines.map((l: any) => ({ t: 'line', cells: [formatDate(l.date), l.label, money(l.amount)] }) as Row),
      ]),
    },
    {
      key: 'contractorPay', title: 'Contractor pay earned', total: sec.contractorPay.total, cols: '1fr 64px 84px', right: [1, 2],
      rows: sec.contractorPay.lines.map((l: any) => ({ t: 'line', cells: [l.label, dim(jobs(l.count)), money(l.amount)] })),
    },
    {
      key: 'commission', title: 'Commission', total: sec.commission.total, cols: '1fr 64px 84px', right: [1, 2],
      rows: sec.commission.lines.map((l: any) => ({ t: 'line', cells: [l.label, dim(jobs(l.count)), money(l.amount)] })),
    },
    {
      key: 'draws', title: 'Partner draws', total: sec.draws.total, cols: '1fr 64px 84px', right: [1, 2],
      rows: [
        ...sec.draws.lines.map((l: any) => ({ t: 'line', cells: [l.label, dim(jobs(l.count, 'draw')), money(l.amount)] }) as Row),
        { t: 'note', text: 'Draws come out of the partner balance, never out of profit.' },
      ],
    },
  ];
  const right: Section[] = [];
  if (s.standing) {
    const st = s.standing;
    right.push({
      key: 'standing', title: `Where we stand · ${formatDate(st.asOf)}`,
      rows: [
        { t: 'kv', label: 'Bank', amount: st.bank, strong: true },
        { t: 'kv', label: 'To collect', amount: st.toCollect, strong: true },
        { t: 'kv', label: 'Owed to contractors', amount: st.contractorsOwed.total, strong: true },
        ...st.contractorsOwed.lines.map((l: any) => ({ t: 'kv', label: l.label, amount: l.amount, indent: true }) as Row),
        { t: 'kv', label: 'Commission owed', amount: st.commissionOwed.total, strong: true },
        ...st.commissionOwed.lines.map((l: any) => ({ t: 'kv', label: l.label, amount: l.amount, indent: true }) as Row),
      ],
    });
  } else {
    right.push({ key: 'standing', title: 'Where we stand', rows: [{ t: 'note', text: 'Before the books cutover — see the monthly books.' }] });
  }
  right.push({
    key: 'partners', title: 'Partners',
    rows: [
      ...s.partners.flatMap((p: any) => [
        { t: 'sub', label: `${p.partner} · ${p.pct}%` } as Row,
        { t: 'kv', label: 'Share of profit', amount: p.share, indent: true } as Row,
        { t: 'kv', label: 'Draws', amount: p.draws, indent: true } as Row,
        { t: 'kv', label: 'Balance', amount: p.balance, indent: true, strong: true } as Row,
      ]),
      { t: 'note', text: 'Balance assumes all money due is paid.' },
    ],
  });
  return { left, right };
}

// ---- Rendering ----------------------------------------------------------------
const clip: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
// Heights include borders: the pagination adds up exactly these numbers.
const bb: React.CSSProperties = { boxSizing: 'border-box' };

function PieceView({ piece, first }: { piece: Piece; first: boolean }) {
  const s = piece.section;
  return (
    <div style={{ marginTop: first ? 0 : H.gap }}>
      <div style={{ ...bb, height: H.head, display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px', background: NAVY, color: '#fff', fontWeight: 700, fontSize: 11.5, letterSpacing: 0.2 }}>
        <span style={{ ...clip, flex: 1 }}>{s.title}{piece.continued ? <span style={{ fontWeight: 400, opacity: 0.8 }}> (continued)</span> : null}</span>
        {s.total != null && <span style={{ fontFamily: MONO }}>{money(s.total)}</span>}
      </div>
      {!s.rows.length && <div style={{ ...bb, height: H.empty, lineHeight: `${H.empty}px`, padding: '0 8px', fontSize: 10.5, color: MUTED, fontStyle: 'italic' }}>Nothing this month.</div>}
      {piece.rows.map((r, i) => {
        if (r.t === 'line') {
          return (
            <div key={i} style={{ ...bb, height: H.line, display: 'grid', gridTemplateColumns: s.cols, gap: 8, alignItems: 'center', padding: '0 8px', fontSize: 10.5, color: INK, borderBottom: `1px solid ${RULE}` }}>
              {r.cells.map((c, k) => (
                <span key={k} style={{ ...clip, textAlign: s.right?.includes(k) ? 'right' : 'left', fontFamily: s.right?.includes(k) && k === r.cells.length - 1 ? MONO : undefined }}>{c}</span>
              ))}
            </div>
          );
        }
        if (r.t === 'sub') {
          return (
            <div key={i} style={{ ...bb, height: H.sub, display: 'flex', alignItems: 'flex-end', gap: 8, padding: '0 8px 2px', fontSize: 10.5, fontWeight: 700, color: NAVY, borderBottom: `1px solid ${NAVY}` }}>
              <span style={{ ...clip, flex: 1 }}>{r.label}</span>
              {r.amount != null && <span style={{ fontFamily: MONO }}>{money(r.amount)}</span>}
            </div>
          );
        }
        if (r.t === 'kv') {
          return (
            <div key={i} style={{ ...bb, height: H.kv, display: 'flex', alignItems: 'center', gap: 8, padding: `0 8px 0 ${r.indent ? 20 : 8}px`, fontSize: r.indent ? 10.5 : 11, fontWeight: r.strong ? 700 : 400, color: r.indent && !r.strong ? MUTED : INK, borderBottom: `1px solid ${RULE}` }}>
              <span style={{ ...clip, flex: 1 }}>{r.label}</span>
              <span style={{ fontFamily: MONO, color: (r.amount ?? 0) < 0 ? '#a23123' : undefined }}>{money(r.amount)}</span>
            </div>
          );
        }
        return <div key={i} style={{ ...bb, height: H.note, lineHeight: `${H.note}px`, padding: '0 8px', fontSize: 9.5, color: MUTED, fontStyle: 'italic', ...clip }}>{r.text}</div>;
      })}
    </div>
  );
}

function SlimHeader({ title, page, pages }: { title: string; page: number; pages: number }) {
  return (
    <div style={{ ...bb, height: SLIM, marginBottom: SLIM_GAP, display: 'flex', alignItems: 'center', borderBottom: `1px solid ${RULE}`, fontSize: 10, color: MUTED }}>
      <span style={{ flex: 1 }}><b style={{ color: NAVY }}>{title}</b> · Month-end statement</span>
      <span>Page {page} of {pages}</span>
    </div>
  );
}

function Footer({ text }: { text: string }) {
  return (
    <div style={{ ...bb, position: 'absolute', left: MARGIN, right: MARGIN, bottom: MARGIN, height: FOOT, display: 'flex', alignItems: 'flex-end', borderTop: `1px solid ${RULE}`, fontSize: 9.5, color: MUTED }}>
      <span style={{ flex: 1 }}>{text}</span>
      <span>Baltic Van Lines</span>
    </div>
  );
}

function Tile({ label, value, amber }: { label: string; value: number; amber?: boolean }) {
  return (
    <div style={{ flex: 1, height: TILES, boxSizing: 'border-box', padding: '10px 12px', border: `1px solid ${amber ? '#f1d39a' : RULE}`, background: amber ? '#fff3dc' : '#fff', borderRadius: 6 }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, color: amber ? '#8a5308' : MUTED, textTransform: 'uppercase', letterSpacing: 0.4, ...clip }}>{label}</div>
      <div style={{ marginTop: 8, fontSize: 18, fontWeight: 700, fontFamily: MONO, color: value < 0 ? '#a23123' : NAVY, ...clip }}>{formatMoney(value)}</div>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  width: PAGE_W, height: PAGE_H, padding: MARGIN, boxSizing: 'border-box', position: 'relative', overflow: 'hidden',
  background: '#fff', color: INK, fontFamily: "Inter, 'Helvetica Neue', Arial, sans-serif",
};

export function StatementPrint({ month, autoPrint }: { month: string; autoPrint: boolean }) {
  const api = useApiClient();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getStatement>>['data'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.getStatement(month).then((r) => { if (alive) setData(r.data ?? null); }).catch((e: any) => { if (alive) setError(e?.message || 'Could not load the statement'); });
    return () => { alive = false; };
  }, [api, month]);

  const s = data?.statement;
  const layout = useMemo(() => {
    if (!s) return null;
    const { left, right } = buildSections(s);
    const r = paginate(right, () => CAP_FIRST, 1);
    const l = paginate([...left, ...r.rest], (i) => (i === 0 ? CAP_FIRST : CAP_REST));
    return { leftFrames: l.frames, rightFrame: r.frames[0] };
  }, [s]);

  useEffect(() => {
    if (!s) return;
    document.title = `Statement ${s.title}${data?.closed ? '' : ' (draft)'} – BVL CRM`;
    if (!autoPrint) return;
    const go = () => setTimeout(() => window.print(), 250);
    (document as any).fonts?.ready ? (document as any).fonts.ready.then(go) : go();
  }, [s, autoPrint]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <div style={{ padding: 40, fontFamily: 'sans-serif', color: '#a23123' }}>{error}</div>;
  if (!s || !layout) return <div style={{ padding: 40, fontFamily: 'sans-serif', color: MUTED }}>Loading the statement…</div>;

  const closed = data?.closed;
  const pages = layout.leftFrames.length;
  const footer = closed
    ? `Closed by ${closed.byName} on ${formatDate(String(closed.at).slice(0, 10))}`
    : `Draft — live figures as of ${formatDateTime(s.generatedAt)}. Not closed.`;
  const changes = data?.changes ?? [];

  return (
    <div className="statement-doc">
      <style>{`
        @page { size: letter; margin: 0; }
        html, body { margin: 0; background: #e9ebef; }
        .statement-doc { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .statement-page { margin: 0 auto 24px; box-shadow: 0 2px 10px rgba(16,24,40,.15); }
        @media print {
          html, body { background: #fff; }
          .no-print { display: none !important; }
          .statement-page { margin: 0; box-shadow: none; break-after: page; page-break-after: always; }
          .statement-page:last-child { break-after: auto; page-break-after: auto; }
        }
      `}</style>

      <div className="no-print" style={{ maxWidth: PAGE_W, margin: '0 auto', padding: '16px 0', fontFamily: 'sans-serif' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <a href="/accounting" style={{ color: NAVY, fontSize: 13 }}>← Accounting</a>
          <ReportTabs month={month} active="statement" />
          <div style={{ flex: 1 }} />
          <button onClick={() => window.print()} style={{ padding: '7px 14px', background: NAVY, color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>Print / Save as PDF</button>
        </div>
        {closed && changes.length > 0 && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: '#fff3dc', border: '1px solid #f1d39a', borderRadius: 8, fontSize: 12.5, color: '#8a5308' }}>
            <b>Changed since close.</b> This statement shows the figures as closed; the live figures now differ:
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {changes.map((c) => <li key={c.label}>{c.label}: {formatMoney(c.closed)} → {formatMoney(c.live)} ({c.delta > 0 ? '+' : ''}{formatMoney(c.delta)})</li>)}
            </ul>
          </div>
        )}
      </div>

      {layout.leftFrames.map((frame, pi) => (
        <div key={pi} className="statement-page" style={pageStyle}>
          <SlimHeader title={s.title} page={pi + 1} pages={pages} />
          {pi === 0 && (
            <>
              <div style={{ height: BAND, marginBottom: BAND_GAP, boxSizing: 'border-box', background: NAVY, borderRadius: 6, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: AMBER, fontSize: 10.5, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase' }}>Month-end statement</div>
                  <div style={{ color: '#fff', fontSize: 28, fontWeight: 700, marginTop: 4 }}>{s.title}</div>
                  <div style={{ color: '#c9d3e8', fontSize: 11, marginTop: 2 }}>Baltic Van Lines · {formatDate(s.from)} – {formatDate(s.to)}</div>
                </div>
                <img src="/brand/logo-horizontal-notag-white.svg" alt="Baltic Van Lines" style={{ height: 28, width: 'auto', display: 'block', flexShrink: 0 }} />
                <div style={{ border: `2px solid ${closed ? AMBER : '#c9d3e8'}`, color: closed ? AMBER : '#fff', borderRadius: 5, padding: '6px 10px', fontSize: 11, fontWeight: 700, letterSpacing: 1.2, whiteSpace: 'nowrap', transform: 'rotate(-3deg)' }}>
                  {closed ? 'CLOSED · LOCKED' : 'DRAFT · LIVE FIGURES'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginBottom: TILES_GAP }}>
                <Tile label="Gross income" value={s.tiles.grossIncome} />
                <Tile label="Expenses + pay + commission" value={s.tiles.costs} />
                <Tile label="Profit to share" value={s.tiles.profit} amber />
                <Tile label="Net after draws" value={s.tiles.netAfterDraws} />
              </div>
              <div style={{ display: 'flex', gap: COL_GAP }}>
                <div style={{ width: LEFT_W, flexShrink: 0 }}>{frame.map((p, k) => <PieceView key={k} piece={p} first={k === 0} />)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>{layout.rightFrame.map((p, k) => <PieceView key={k} piece={p} first={k === 0} />)}</div>
              </div>
            </>
          )}
          {pi > 0 && frame.map((p, k) => <PieceView key={k} piece={p} first={k === 0} />)}
          <Footer text={footer} />
        </div>
      ))}
    </div>
  );
}
